/**
 * CONVERT AN INGREDIENT INTO A SAVED PREPARATION
 *
 * The case this exists for: the kitchen has "Garam Masala" as an ingredient (12 recipes use
 * it) AND "Garam Masala Indian" as a saved preparation (6 spices, 500 g a batch). They are
 * the same thing. Binding them makes every one of those 12 recipes deduct the six spices and
 * cost from their prices — without editing a single recipe, because the recipes keep pointing
 * at the ingredient. The ingredient stays as the face; the preparation becomes the engine.
 *
 * Three deliberate choices in this screen:
 *
 *   1. NOTHING IS CONVERTED WITHOUT A PREVIEW. The server re-runs every validation and
 *      answers with either a refusal written for a person or a summary of what will change.
 *      The preview IS that call, so the confirmation can never disagree with the outcome.
 *   2. REFUSALS ARE SHOWN VERBATIM. "Garam Masala still has 1,500 g of purchased stock…"
 *      names the thing to fix. Replacing it with "cannot convert" would send the user to
 *      support for something they can resolve in a minute.
 *   3. A CROSS-FAMILY PAIR ASKS FOR THE FACTOR rather than guessing. If the ingredient is in
 *      pieces and the batch in grams, "1 piece = ? g" is a fact about the kitchen; assuming 1
 *      would misdeduct by a factor of hundreds on every sale, silently.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';
import http from '../services/httpClient';
import './StructuralActionLogModal.css';
import './IngredientReconciliationRegistry.css';

/**
 * A searchable select built from a button and a div list.
 *
 * It carries the combobox/listbox/option roles explicitly. Without them the control has no
 * accessible name, no announced state and no options a screen reader can enumerate — it is a
 * button that says "Select an ingredient…" and a pile of unlabelled divs. `id` exists so the
 * visible <label> can point at it with htmlFor, which makes the accessible name and the
 * visible label the same string rather than two things that can drift apart.
 */
function CustomSelect({ id, value, onChange, options, placeholder, showSearch = true, direction = 'auto' }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [dropdownPos, setDropdownPos] = useState({ top: 0, bottom: 'auto', left: 0, width: 0, isUpward: false });
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target)
      ) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  useEffect(() => {
    if (isOpen && searchRef.current) {
      setTimeout(() => searchRef.current && searchRef.current.focus(), 50);
    }
  }, [isOpen]);

  const handleToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const dropdownHeight = 280;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      
      const openUpward = direction === 'up' || (direction === 'auto' && spaceBelow < dropdownHeight && spaceAbove > spaceBelow);
      
      if (openUpward) {
        setDropdownPos({
          bottom: window.innerHeight - rect.top + 4,
          top: 'auto',
          left: rect.left,
          width: rect.width,
          isUpward: true,
        });
      } else {
        setDropdownPos({
          top: rect.bottom + 4,
          bottom: 'auto',
          left: rect.left,
          width: rect.width,
          isUpward: false,
        });
      }
    }
    setIsOpen((prev) => !prev);
    if (isOpen) setSearchQuery('');
  };

  const selectedOption = options.find(opt => String(opt.value) === String(value));

  const filteredOptions = options.filter(opt =>
    opt.label.toLowerCase().includes(searchQuery.toLowerCase())
  ).sort((a, b) => {
    const q = searchQuery.toLowerCase();
    if (!q) return 0;
    const aStarts = a.label.toLowerCase().startsWith(q);
    const bStarts = b.label.toLowerCase().startsWith(q);
    if (aStarts && !bStarts) return -1;
    if (!aStarts && bStarts) return 1;
    return 0;
  });

  return (
    <div className="irr-custom-select-container">
      <button
        type="button"
        id={id}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        className="irr-custom-select-trigger"
        ref={triggerRef}
        onClick={handleToggle}
        style={{ width: '100%', boxSizing: 'border-box' }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'left', flex: 1 }}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown size={16} style={{ flexShrink: 0, marginLeft: '8px' }} />
      </button>
      {isOpen && (
        <div
          ref={dropdownRef}
          role="listbox"
          className={`irr-custom-select-options ${dropdownPos.isUpward ? 'irr-custom-select-options--up' : ''}`}
          style={{
            position: 'fixed',
            top: dropdownPos.isUpward ? 'auto' : `${dropdownPos.top}px`,
            bottom: dropdownPos.isUpward ? `${dropdownPos.bottom}px` : 'auto',
            left: `${dropdownPos.left}px`,
            width: `${dropdownPos.width}px`,
            zIndex: 99999,
            backgroundColor: '#ffffff',
            boxShadow: dropdownPos.isUpward
              ? '0 -10px 25px -5px rgba(0, 0, 0, 0.15), 0 -8px 10px -6px rgba(0, 0, 0, 0.1)'
              : '0 10px 25px -5px rgba(0, 0, 0, 0.15), 0 8px 10px -6px rgba(0, 0, 0, 0.1)'
          }}
        >
          {showSearch && (
            <div className="irr-custom-select-search-wrap">
              <Search size={14} className="irr-custom-select-search-icon" />
              <input
                ref={searchRef}
                type="text"
                className="irr-custom-select-search-input"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onMouseDown={(e) => e.stopPropagation()}
              />
              {searchQuery && (
                <button
                  type="button"
                  className="irr-custom-select-search-clear"
                  onMouseDown={(e) => { e.preventDefault(); setSearchQuery(''); searchRef.current && searchRef.current.focus(); }}
                >
                  <X size={12} />
                </button>
              )}
            </div>
          )}
          <div className="irr-custom-select-options-list">
            {filteredOptions.length === 0 ? (
              <div className="irr-custom-select-no-results">No results found</div>
            ) : (
              filteredOptions.map((option) => (
                <div
                  key={option.value}
                  role="option"
                  aria-selected={String(option.value) === String(value)}
                  className={`irr-custom-select-option${String(option.value) === String(value) ? ' irr-custom-select-option--selected' : ''}`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onChange(option.value);
                    setIsOpen(false);
                    setSearchQuery('');
                  }}
                >
                  {option.label}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** Server errors are already written for people; only a transport failure needs wording. */
function errorText(err, fallback) {
  const body = (err && (err.data || (err.response && err.response.data))) || {};
  if (typeof body.error === 'string' && body.error.trim()) return body.error.trim();
  if (err && err.message) return err.message;
  return fallback;
}

const sortByName = (rows, key = 'name') =>
  [...rows].sort((a, b) => String(a[key] || '').localeCompare(String(b[key] || '')));

const ConvertToPreparationModal = ({ isOpen, onClose, onConverted }) => {
  const [loading, setLoading] = useState(true);
  const [candidates, setCandidates] = useState({ preparations: [], items: [], converted: [] });
  const [itemId, setItemId] = useState('');
  const [prepId, setPrepId] = useState('');
  const [factor, setFactor] = useState('');
  const [needsFactor, setNeedsFactor] = useState(false);
  const [preview, setPreview] = useState(null);
  const [problem, setProblem] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [itemSearch, setItemSearch] = useState('');
  // The leftover-stock refusal, kept as data rather than a sentence, because it is the one
  // refusal the user can answer from this screen.
  const [strandedStock, setStrandedStock] = useState(null);
  const [settleStock, setSettleStock] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setProblem('');
    try {
      const res = await http.get('/medium-features/decomposition/convert/candidates');
      setCandidates(res && res.data ? res.data : { preparations: [], items: [], converted: [] });
    } catch (err) {
      setProblem(errorText(err, 'Could not load ingredients and preparations.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    load();
    // A fresh visit starts from nothing chosen; a stale preview from last time would describe
    // a pair that is no longer selected.
    setItemId(''); setPrepId(''); setFactor(''); setPreview(null);
    setProblem(''); setNeedsFactor(false); setNotice(''); setItemSearch('');
  }, [isOpen, load]);

  const items = useMemo(() => {
    const all = sortByName(candidates.items || []);
    const q = itemSearch.trim().toLowerCase();
    if (!q) return all;
    return all.filter((i) => String(i.name || '').toLowerCase().includes(q));
  }, [candidates.items, itemSearch]);

  const preparations = useMemo(() => sortByName(candidates.preparations || []), [candidates.preparations]);
  const selectedItem = useMemo(
    () => (candidates.items || []).find((i) => String(i.item_id) === String(itemId)) || null,
    [candidates.items, itemId]
  );
  const selectedPrep = useMemo(
    () => (candidates.preparations || []).find((p) => String(p.id) === String(prepId)) || null,
    [candidates.preparations, prepId]
  );

  /**
   * Ask the server whether this pair may be converted, and what changes if it is.
   *
   * The settle flag is passed explicitly rather than read from state: the settle panel calls
   * this in the same tick it turns settling on, and a state read there would still see
   * `false` and re-show the refusal the user just answered.
   *
   * It is a NAMED option, not a positional argument, and that is deliberate. As a positional
   * one, `onClick={runPreview}` hands it a click event — truthy — and silently authorises a
   * write-off from a button that means "check this pair". A flag that destroys stock must not
   * be reachable by passing the wrong thing.
   */
  const runPreview = useCallback(async ({ settle = settleStock } = {}) => {
    if (!itemId || !prepId) return;
    setBusy(true);
    setProblem('');
    setPreview(null);
    try {
      const res = await http.post('/medium-features/decomposition/convert/preview', {
        itemId: Number(itemId),
        prepId: Number(prepId),
        ...(factor ? { factor: Number(factor) } : {}),
        ...(settle ? { settleStock: true } : {}),
      });
      setPreview(res && res.data ? res.data : null);
      setNeedsFactor(false);
      // Kept when the pass only succeeded BECAUSE settling was authorised — the panel holding
      // that authorisation has to stay on screen for the user to take it back.
      setStrandedStock((prev) => (settle ? prev : null));
    } catch (err) {
      const body = (err && (err.data || (err.response && err.response.data))) || {};
      // The two refusals that are a question rather than a dead end.
      setNeedsFactor(body.code === 'CROSS_FAMILY_NO_FACTOR');
      setStrandedStock(
        (body.code === 'STOCK_NOT_ZERO' || body.code === 'STOCK_NEGATIVE') && body.details
          ? { ...body.details, code: body.code }
          : null
      );
      setProblem(errorText(err, 'Could not check this pair.'));
    } finally {
      setBusy(false);
    }
  }, [itemId, prepId, factor, settleStock]);

  // Re-check whenever the pair changes. The user should not have to press anything to learn
  // that the stock rule blocks this ingredient.
  useEffect(() => {
    // A settlement authorised for one pair must not carry over to the next one the user picks.
    setSettleStock(false);
    setStrandedStock(null);
    if (itemId && prepId) runPreview({ settle: false });
    else { setPreview(null); setProblem(''); setNeedsFactor(false); }
    // runPreview is intentionally omitted: it changes with `factor`, and re-previewing on
    // every keystroke of the factor field would fight the user as they type.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId, prepId]);

  const convert = async () => {
    setBusy(true);
    setProblem('');
    try {
      const res = await http.post('/medium-features/decomposition/convert', {
        itemId: Number(itemId),
        prepId: Number(prepId),
        ...(factor ? { factor: Number(factor) } : {}),
        // Sent only when the user chose it against a figure this screen showed them. The
        // server re-checks the stock itself; this flag is permission, not instruction.
        ...(settleStock ? { settleStock: true } : {}),
      });
      setNotice(res && res.message ? res.message : 'Converted.');
      setItemId(''); setPrepId(''); setFactor(''); setPreview(null);
      setSettleStock(false); setStrandedStock(null);
      await load();
      if (typeof onConverted === 'function') onConverted();
    } catch (err) {
      setProblem(errorText(err, 'Could not convert.'));
    } finally {
      setBusy(false);
    }
  };

  const unlink = async (row) => {
    setBusy(true);
    setProblem('');
    try {
      const res = await http.post('/medium-features/decomposition/unlink', { itemId: row.item_id });
      setNotice(res && res.message ? res.message : 'Reverted to a purchased ingredient.');
      await load();
      if (typeof onConverted === 'function') onConverted();
    } catch (err) {
      setProblem(errorText(err, 'Could not undo this link.'));
    } finally {
      setBusy(false);
    }
  };

  if (!isOpen) return null;

  const label = { display: 'block', fontSize: '12.5px', color: '#4b5563', marginBottom: '4px', fontWeight: 600 };
  const field = { width: '100%', padding: '9px 10px', borderRadius: '8px', border: '1px solid #d5dbe6', fontSize: '13.5px' };

  return (
    <div className="modal-overlay">
      <div className="modal-content structural-log-modal">
        <div className="modal-header">
          <h2>Make an Ingredient from a Preparation</h2>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body">
          <div
            className="log-instructions"
            style={{
              background: 'linear-gradient(135deg, #f0f4ff 0%, #f8faff 100%)',
              padding: '12px 14px', borderRadius: '10px', marginBottom: '16px',
              fontSize: '13px', color: '#4b5563', border: '1px solid #dde5ff', lineHeight: 1.55,
            }}
          >
            Pick an ingredient your recipes already use, and the saved preparation it is really
            made from. Every recipe keeps pointing at the ingredient — but from then on a sale
            deducts the preparation's components and the cost follows their prices.
          </div>

          {notice && (
            <div className="log-instructions" style={{
              background: '#ecfdf5', border: '1px solid #a7f3d0', color: '#065f46',
              padding: '10px 12px', borderRadius: '8px', marginBottom: '14px', fontSize: '13px',
            }}>
              {notice}
            </div>
          )}

          {loading ? (
            <div className="structural-spinner-container">
              <div className="structural-spinner"></div>
              <div className="loading-text">Loading…</div>
            </div>
          ) : (
            <>
              <div style={{ display: 'grid', gap: '14px', marginBottom: '14px' }}>
                <div>
                  <label style={label} htmlFor="ctp-ingredient">Ingredient (used by your recipes)</label>
                  <CustomSelect
                    id="ctp-ingredient"
                    value={itemId}
                    onChange={(val) => setItemId(val)}
                    placeholder="Select an ingredient…"
                    direction="up"
                    options={items.map((i) => ({
                      value: i.item_id,
                      label: `${i.name}${i.recipe_count ? ` — ${i.recipe_count} recipe(s)` : ''}${Number(i.current_stock) ? ` — ${Number(i.current_stock)} ${i.unit_symbol || ''} in stock` : ''}`
                    }))}
                  />
                </div>

                <div>
                  <label style={label} htmlFor="ctp-preparation">Saved preparation it is made from</label>
                  <CustomSelect
                    id="ctp-preparation"
                    value={prepId}
                    onChange={(val) => setPrepId(val)}
                    placeholder="Select a preparation…"
                    direction="up"
                    options={preparations.map((p) => ({
                      value: p.id,
                      label: `${p.name} — makes ${Number(p.total_weight)} ${p.unit_symbol} from ${p.component_count} ingredient(s)`
                    }))}
                  />
                  {preparations.length === 0 && (
                    <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '6px' }}>
                      Every saved preparation is already linked to an ingredient.
                    </div>
                  )}
                </div>

                {needsFactor && (
                  <div>
                    <label style={label}>
                      How many {selectedPrep?.unit_symbol} is one {selectedItem?.unit_symbol || 'unit'}?
                    </label>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <input
                        style={field}
                        type="number"
                        min="0"
                        step="any"
                        value={factor}
                        onChange={(e) => setFactor(e.target.value)}
                        aria-label="Conversion factor"
                      />
                      {/* Wrapped, not passed directly: onClick hands the button a click
                          event, and runPreview's first argument authorises a write-off. */}
                      <button className="revert-btn" onClick={() => runPreview()} disabled={busy || !factor}>
                        Check
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {problem && (
                <div className="error-message" style={{ marginBottom: '14px' }}>{problem}</div>
              )}

              {/* The leftover-stock refusal, with the action that answers it. Without this the
                  message ("use it up or write it off first") names a fix the user has no way
                  to carry out — and none at all when the figure is negative. */}
              {strandedStock && (
                <div style={{
                  border: '1px solid #fcd9a4', background: '#fffbf2', borderRadius: '10px',
                  padding: '12px 14px', marginBottom: '14px', fontSize: '13px', color: '#7c4a03',
                }}>
                  <div style={{ fontWeight: 700, marginBottom: '6px' }}>
                    {strandedStock.code === 'STOCK_NEGATIVE'
                      ? 'This ingredient is showing less than zero'
                      : 'This ingredient still holds stock'}
                  </div>
                  <div style={{ lineHeight: 1.55 }}>
                    Rolled-up stock {Number(strandedStock.currentStock)} {strandedStock.unit},
                    {' '}batches {Number(strandedStock.batchRemaining)} {strandedStock.unit}
                    {strandedStock.openBatches ? ` across ${strandedStock.openBatches} batch(es)` : ''}.
                    {strandedStock.costImpact
                      ? ` Valued at ₹${Number(strandedStock.costImpact).toFixed(2)}.`
                      : ''}
                  </div>
                  <label style={{
                    display: 'flex', gap: '8px', alignItems: 'flex-start',
                    marginTop: '10px', cursor: 'pointer',
                  }}>
                    <input
                      type="checkbox"
                      checked={settleStock}
                      onChange={(e) => {
                        const on = e.target.checked;
                        setSettleStock(on);
                        runPreview({ settle: on });
                      }}
                      style={{ marginTop: '2px' }}
                    />
                    <span>
                      {strandedStock.code === 'STOCK_NEGATIVE'
                        ? 'Settle this shortfall to zero as part of converting.'
                        : 'Write this stock off as part of converting.'}
                      {' '}It is recorded in the action log and comes back in full if the
                      conversion is undone.
                    </span>
                  </label>
                </div>
              )}

              {preview && (
                <div style={{
                  border: '1px solid #dde5ff', borderRadius: '10px', padding: '12px 14px',
                  marginBottom: '14px', fontSize: '13px', color: '#374151', background: '#fbfcff',
                }}>
                  <div style={{ fontWeight: 700, marginBottom: '6px' }}>
                    "{preview.itemName}" will be made from "{preview.prepName}"
                  </div>
                  <div>
                    One batch makes {preview.prepYield} {preview.prepYieldUnit} from {preview.componentCount} ingredient(s).
                  </div>
                  <div style={{ marginTop: '4px' }}>
                    {preview.recipeCount} recipe(s)
                    {preview.complimentaryCount ? ` and ${preview.complimentaryCount} complimentary item(s)` : ''}
                    {' '}will start deducting those ingredients instead. No recipe is edited.
                  </div>
                  {preview.crossFamily && (
                    <div style={{ marginTop: '6px', color: '#b45309' }}>
                      Using your factor: 1 {preview.itemUnit} = {preview.factor} {preview.prepYieldUnit}.
                    </div>
                  )}
                  {preview.willSettle && (
                    <div style={{ marginTop: '6px', color: '#b45309', fontWeight: 600 }}>
                      {preview.willSettle.direction === 'shortfall'
                        ? `Its shortfall of ${Math.abs(preview.willSettle.quantity)} ${preview.willSettle.unit} will be settled to zero.`
                        : `${preview.willSettle.quantity} ${preview.willSettle.unit} of leftover stock will be written off`
                          + (preview.willSettle.costImpact
                            ? ` (₹${preview.willSettle.costImpact.toFixed(2)}).`
                            : '.')}
                    </div>
                  )}
                  <button
                    className="revert-btn"
                    style={{ marginTop: '10px' }}
                    onClick={convert}
                    disabled={busy}
                  >
                    {busy
                      ? 'Converting…'
                      : preview.willSettle
                        ? (preview.willSettle.direction === 'shortfall' ? 'Settle and convert' : 'Write off and convert')
                        : 'Convert'}
                  </button>
                </div>
              )}

              {(candidates.converted || []).length > 0 && (
                <div>
                  <h3 style={{ fontSize: '13.5px', color: '#374151', margin: '18px 0 8px' }}>
                    Already made in-house
                  </h3>
                  <div className="logs-list">
                    {candidates.converted.map((row) => (
                      <div key={`${row.item_id}-${row.prep_id}`} className="log-card">
                        <div className="log-details flex-details">
                          <div className="from-side"><strong>{row.item_name}</strong></div>
                          <div className="to-side">made from {row.prep_name}</div>
                        </div>
                        <div className="log-actions">
                          <button className="revert-btn" onClick={() => unlink(row)} disabled={busy}>
                            We buy this again
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConvertToPreparationModal;
